import { Component, Mixins } from 'vue-property-decorator'
import IconsMixin from '@/mixins/IconsMixin.vue'
import AuthMixin from '@/mixins/AuthMixin.vue'
import UserCompaniesMixin from '@/mixins/UserCompaniesMixin.vue'
import LoadingPage from '@/components/LoadingPage.vue'
import SaveButton from '@/components/SaveButton.vue'
import LabeledValue from '@/components/LabeledValue.vue'
import ProductSelector from '@/components/Order/ProductSelector.vue'
import {
    CompanyCollection,
    FavoriteList,
    FavoriteListCollection,
    IVendorPriceListEntry,
    MasterCatalogCollection,
    PriceList,
    PriceListCollection,
    PriceListEntry,
    ReminderCollection
} from '@/firestore'
import store, { Actions } from '../../../store'
import PriceListSelector from '@/components/Order/PriceListSelector.vue'
import QuantityComboBox from '@/components/QuantityComboBox.vue'
import CategorySelectionModal from '@/components/Order/CategorySelectionModal.vue'

@Component({
    components: {
        SaveButton,
        LoadingPage,
        LabeledValue,
        ProductSelector,
        QuantityComboBox,
        PriceListSelector,
        CategorySelectionModal
    }
})
export default class FavoriteListEdit extends Mixins(IconsMixin, AuthMixin, UserCompaniesMixin) {
    public isSaving: boolean = false
    public errors: string[] = []
    public isLoading: boolean = false
    public displayHelperText: boolean = false
    public favoriteList: FavoriteList | null = null
    public priceLists: PriceList[] = []
    public priceListEntries: IVendorPriceListEntry[] = []

    public reminderMap: { [entryIdAndVendorId: string]: string[] } = {['']: ['']}

    public $refs!: {
        form: HTMLFormElement
    }

    //METHODS
    public async mounted() {
        this.isLoading = true
        try {
            let favoriteList = await this.lookupFavoriteList()
            if (!favoriteList) {
                favoriteList = new FavoriteList(this.userCompany.id)
            }

            this.favoriteList = favoriteList

            if (!this.favoriteList.entries || !this.favoriteList.entries.length) {
                this.addFavorite()
            }

            await this.setReminders()
            await this.populatePriceListEntries()

        } catch (error) {
            const err = new Error('You do not have permissions to view that page')
            return store.dispatch(Actions.SET_ERROR, [err, this.$router])
        }
        this.isLoading = false
    }

    public async save() {
        this.errors = []
        await this.$nextTick()

        this.isSaving = true
        await this.updateFavoriteList()
        this.isSaving = false

        await this.$router.push({name: 'favorite-list-detail'})
    }

    public isFavoriteListValid(favoriteList: FavoriteList | null): favoriteList is FavoriteList {
        if (!favoriteList) {
            return false
        }

        if (!this.$refs.form.validate()) {
            if (!favoriteList.entries.every(entry => entry.vendorCompanyId)) {
                this.errors.push("You may not add products to your Favorite List which don't have a vendor attached")
            }
            if (!favoriteList.entries.every(entry => entry.description)) {
                this.errors.push("Please enter a description for every item")
            }
        }

        if (!favoriteList.entries.every(entry => entry.category)) {
            this.errors.push("Please select a category for every entry")
            return false
        }

        return !!this.$refs.form.validate()
            && favoriteList instanceof FavoriteList
            && !this.errors.length
    }

    public async updateFavoriteList() {
        if (this.favoriteList) {
            const favoriteListCollection = new FavoriteListCollection((await this.$firebase).firestore())
            await favoriteListCollection.save({
                ...this.favoriteList,
                entries: this.favoriteList.entries.filter(entry =>
                    entry.upc
                    || entry.description
                    || entry.barcode
                    || entry.orderingNotes
                    || entry.category
                )
            } as FavoriteList)
        }
    }

    public async updateDetails(entry: IVendorPriceListEntry, updateEntry: IVendorPriceListEntry) {
        if (!updateEntry) {
            return
        }

        entry.id = updateEntry.id
        entry.vendorCompanyId = updateEntry.vendorCompanyId
        entry.priceListId = updateEntry.priceListId
        entry.upc = updateEntry.upc
        entry.description = updateEntry.description
        entry.orderingNotes = updateEntry.orderingNotes
        entry.category = updateEntry.category
        entry.vendorCompanyName = updateEntry.vendorCompanyName ? updateEntry.vendorCompanyName : await this.getVendorName(updateEntry)
    }

    public addFavorite() {
        if (!this.favoriteList) {
            return
        }

        const entry = new PriceListEntry('', '')
        this.favoriteList.entries.unshift(entry as unknown as IVendorPriceListEntry)
    }

    public removeEntry(entry: IVendorPriceListEntry) {
        if (!this.favoriteList) {
            return
        }

        const index = this.favoriteList.entries.indexOf(entry)
        if (index !== -1) {
            this.favoriteList.entries.splice(index, 1)
        }
    }

    public getCategory(entry: IVendorPriceListEntry) {
        if (!entry || !entry.category) {
            return 'N/A'
        }

        const lineageArray = entry.category.split(' > ')
        return lineageArray[lineageArray.length - 1].trim()
    }

    public async getVendorName(entry: IVendorPriceListEntry) {
        if (!entry || !entry.vendorCompanyId) {
            return 'N/A'
        }

        const companyCollection = new CompanyCollection((await this.$firebase).firestore())
        const company = await companyCollection.getCompany(entry.vendorCompanyId)

        if (company) {
            return company.displayName
        }
    }

    private async setReminders() {
        const reminderCollection = new ReminderCollection((await this.$firebase).firestore())
        const reminders = await reminderCollection.listRemindersByRestaurantCompanyId(this.userCompany.id)
        reminders.forEach(reminder => {
            if (
                this.reminderMap[reminder.priceListEntryId + reminder.vendorCompanyId]
                && !this.reminderMap[reminder.priceListEntryId + reminder.vendorCompanyId].includes(reminder.text)
            ) {
                this.reminderMap[reminder.priceListEntryId + reminder.vendorCompanyId].push(reminder.text)
            } else if (!this.reminderMap[reminder.priceListEntryId + reminder.vendorCompanyId]) {
                this.reminderMap[reminder.priceListEntryId + reminder.vendorCompanyId] = [reminder.text]
            }
        })
    }

    private async populatePriceListEntries() {
        const priceListCollection = new PriceListCollection((await this.$firebase).firestore())
        this.priceLists = await priceListCollection.listPriceListsByCompanyId(this.userCompany.id, this.userCompany.type, true)

        const masterCatalogCollection = new MasterCatalogCollection((await this.$firebase).firestore())

        this.priceLists.forEach(async (priceList: PriceList) => {
            priceList.entries.forEach((entry: PriceListEntry) => {
                this.priceListEntries.push({...entry, priceListId: priceList.id, vendorCompanyId: priceList.vendorCompanyId})
            })
            const masterCatalog = await masterCatalogCollection.getByCompanyId(priceList.vendorCompanyId)
            if (masterCatalog && masterCatalog.entries && masterCatalog.entries.length) {
                masterCatalog.entries.forEach((entry: PriceListEntry) => {
                    this.priceListEntries.push({...entry, priceListId: priceList.id, vendorCompanyId: masterCatalog.companyId})
                })
            }
        })
    }

    private async lookupFavoriteList() {
        const favoriteListCollection = new FavoriteListCollection((await this.$firebase).firestore())
        return await favoriteListCollection.findFavoriteListByRestaurant(this.userCompany.id)
    }
}
